import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Mail, Plus, Edit3, Trash2, Eye, Copy, Save,
  ChevronLeft, Code, Clock, Zap, AlertTriangle, Tag, Image, Upload
} from 'lucide-react';
import toast from 'react-hot-toast';
import { emailTemplatesAPI } from '../services/api';

// Template padrão "Atraso superior a 60 dias"
const TEMPLATE_PADRAO_60_DIAS = {
  nome: 'Atraso superior a 60 dias',
  descricao: 'Notificação legal para beneficiários com mais de 60 dias de atraso (Art. 13, lei 9.656/98)',
  assunto: 'NOTIFICAÇÃO - Débitos pendentes Plano de Saúde - {NOME_BENEFICIARIO}',
  categoria: 'inadimplencia',
  envio_automatico: true,
  regra_dias_atraso: 60,
  corpo_html: `<div style="font-family:Arial,sans-serif;max-width:700px;margin:0 auto;padding:20px;">
  <div style="margin-bottom:30px;">
    <img src="[URL DO LOGO DA OPERADORA]" alt="[NOME DA OPERADORA]" style="max-width:200px;margin-bottom:10px;display:block;">
    <h2 style="color:#333;font-size:20px;margin:10px 0 0 0;text-decoration:underline;">NOTIFICAÇÃO</h2>
  </div>

  <p>Prezado(a) Senhor(a) <strong>{NOME_BENEFICIARIO}</strong></p>

  <p>Servimo-nos deste expediente para informar que até a presente data encontram-se pendentes em nossos registros débitos referentes ao seu Plano de Saúde, vinculados ao seu CPF nº <strong>{CPF}</strong> conforme segue abaixo:</p>

  {TABELA_DEBITOS}

  <p style="margin-top:20px;">Notificamos, assim, pela presente, obedecendo às previsões legais (Artigo 13º, alínea ll da lei 9.656/98) e regulamentares, que o "não-pagamento da mensalidade por período superior a sessenta dias, consecutivos ou não, nos últimos doze meses de vigência do contrato" poderá ocasionar a suspensão dos atendimentos e/ou cancelamento do Plano.</p>

  <p>Desta feita, visando evitar a interrupção dos atendimentos e o cancelamento do Plano, notificamos que o pagamento dos valores em atraso ora informados precisa ser efetuado em até <strong>10 (dez) dias</strong> a contar da data de recebimento desta Notificação.</p>

  <p>Esperamos assim seu breve contato via e-mail no <a href="mailto:[E-MAIL DE NEGOCIAÇÃO]">[E-MAIL DE NEGOCIAÇÃO]</a>, ou através do telefone [TELEFONE], ou via WhatsApp através do número [WHATSAPP], ou, mediante comparecimento pessoal em nossa Central de Atendimento ao público, localizada em [ENDEREÇO DA CENTRAL DE ATENDIMENTO], para auxiliá-lo(a) com a regularização dos débitos existentes.</p>

  <p>Importa ainda destacar que o eventual cancelamento do seu Plano de Saúde, pelo não pagamento dos valores em aberto ora informados, não implica em perdão ou quitação automática dos débitos existentes até a data do efetivo cancelamento, os quais poderão ser cobrados, posteriormente, a critério da operadora.</p>

  <p>Uma vez suspenso, a reativação do plano poderá ocorrer no primeiro dia do mês posterior ao cancelamento, mediante a quitação prévia dos débitos, sem que haja necessidade de cumprir carência. Caso a quitação e reativação ocorra após esse período, haverá necessidade de cumprir todas as carências previstas no regulamento.</p>

  <p>Lembramos que o e-mail cadastrado para o recebimento de qualquer comunicação por parte da operadora é de total responsabilidade do beneficiário.</p>

  <p>Caso os débitos em referência tenham sido quitados - antes ou depois de emissão da presente correspondência - pedimos gentilmente que desconsidere seu conteúdo e que contate a operadora (telefone do setor financeiro) para que os registros de cobranças sejam regularizados.</p>

  <p>Estamos à disposição para quaisquer esclarecimentos.</p>

  <p style="margin-top:30px;"><strong>Att. Dep. Financeiro</strong></p>

  <hr style="border:none;border-top:2px solid #20a490;margin:30px 0 15px 0;">
  <div style="font-size:11px;color:#666;text-align:center;">
    <p style="margin:2px 0;">[RAZÃO SOCIAL] – CNPJ [CNPJ]</p>
    <p style="margin:2px 0;">[ENDEREÇO]</p>
    <p style="margin:2px 0;">[CEP – TELEFONES]</p>
    <p style="margin:2px 0;">ANS – N.º [REGISTRO]</p>
  </div>
</div>`,
};

// Dados fictícios para preview local
const DADOS_EXEMPLO = {
  '{NOME_BENEFICIARIO}': 'JOSE VITOR LOPES E SILVA',
  '{CPF}': '358.992.990-01',
  '{TOTAL_VALOR}': 'R$ 18.165,18',
  '{MAIOR_ATRASO}': '134',
  '{QTD_MENSALIDADES}': '6',
  '{EMAIL_BENEFICIARIO}': 'exemplo@email.com',
  '{DATA_ATUAL}': new Date().toLocaleDateString('pt-BR'),
  '{CONTRATO}': '12345',
  '{MATRICULA}': '98765',
  '{TABELA_DEBITOS}': `<table style="width:100%;border-collapse:collapse;font-size:13px;font-family:Arial,sans-serif;">
    <thead><tr style="background:#20a490;color:#fff;">
      <th style="padding:8px 10px;border:1px solid #ddd;text-align:left;">BENEFICIÁRIO</th>
      <th style="padding:8px 10px;border:1px solid #ddd;">CPF</th>
      <th style="padding:8px 10px;border:1px solid #ddd;">BOLETO</th>
      <th style="padding:8px 10px;border:1px solid #ddd;">VALOR</th>
      <th style="padding:8px 10px;border:1px solid #ddd;">REFER</th>
      <th style="padding:8px 10px;border:1px solid #ddd;">ATRASO_DIAS</th>
      <th style="padding:8px 10px;border:1px solid #ddd;">TP_DIVIDA</th>
    </tr></thead>
    <tbody>
      <tr><td style="padding:6px 10px;border:1px solid #ddd;">JOSE VITOR LOPES E SILVA</td><td style="padding:6px 10px;border:1px solid #ddd;text-align:center;">358992990</td><td style="padding:6px 10px;border:1px solid #ddd;text-align:center;">1278715</td><td style="padding:6px 10px;border:1px solid #ddd;text-align:right;">R$ 763,92</td><td style="padding:6px 10px;border:1px solid #ddd;text-align:center;">01/2026</td><td style="padding:6px 10px;border:1px solid #ddd;text-align:center;">0</td><td style="padding:6px 10px;border:1px solid #ddd;">PARTICIPACAO</td></tr>
      <tr><td style="padding:6px 10px;border:1px solid #ddd;">JOSE VITOR LOPES E SILVA</td><td style="padding:6px 10px;border:1px solid #ddd;text-align:center;">358992990</td><td style="padding:6px 10px;border:1px solid #ddd;text-align:center;">1278714</td><td style="padding:6px 10px;border:1px solid #ddd;text-align:right;">R$ 2.746,75</td><td style="padding:6px 10px;border:1px solid #ddd;text-align:center;">01/2026</td><td style="padding:6px 10px;border:1px solid #ddd;text-align:center;">0</td><td style="padding:6px 10px;border:1px solid #ddd;">MENSALIDADE</td></tr>
      <tr><td style="padding:6px 10px;border:1px solid #ddd;">JOSE VITOR LOPES E SILVA</td><td style="padding:6px 10px;border:1px solid #ddd;text-align:center;">358992990</td><td style="padding:6px 10px;border:1px solid #ddd;text-align:center;">1256879</td><td style="padding:6px 10px;border:1px solid #ddd;text-align:right;">R$ 2.857,88</td><td style="padding:6px 10px;border:1px solid #ddd;text-align:center;">11/2025</td><td style="padding:6px 10px;border:1px solid #ddd;text-align:center;">43</td><td style="padding:6px 10px;border:1px solid #ddd;">MENSALIDADE</td></tr>
      <tr style="background:#f5f5f5;font-weight:bold;"><td colspan="3" style="padding:8px 10px;border:1px solid #ddd;text-align:right;">TOTAL</td><td style="padding:8px 10px;border:1px solid #ddd;text-align:right;">R$ 18.165,18</td><td colspan="3" style="padding:8px 10px;border:1px solid #ddd;"></td></tr>
    </tbody></table>`,
};

const previewLocal = (html) => {
  let resultado = html || '';
  Object.entries(DADOS_EXEMPLO).forEach(([chave, valor]) => {
    resultado = resultado.split(chave).join(valor);
  });
  return resultado;
};

const categoriaLabel = (valor) => {
  const map = {
    inadimplencia: 'Cobrança / Inadimplência',
    boas_vindas: 'Boas-vindas',
    cancelamento: 'Cancelamento de Plano',
    reativacao: 'Reativação de Plano',
    geral: 'Notificação Geral',
  };
  return map[valor] || valor;
};

const categoriaColor = (valor) => {
  const map = {
    inadimplencia: { bg: '#fce4ec', color: '#c62828' },
    boas_vindas: { bg: '#e8f5e9', color: '#2e7d32' },
    cancelamento: { bg: '#fff3e0', color: '#e65100' },
    reativacao: { bg: '#e3f2fd', color: '#1565c0' },
    geral: { bg: '#f3e5f5', color: '#6a1b9a' },
  };
  return map[valor] || { bg: '#f5f5f5', color: '#555' };
};

const TemplatesEmail = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [variaveis, setVariaveis] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editorTab, setEditorTab] = useState('code'); // 'code' ou 'preview'
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewAssunto, setPreviewAssunto] = useState('');
  const [previewMatRf, setPreviewMatRf] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testeMatricula, setTesteMatricula] = useState('');
  const [testeHtml, setTesteHtml] = useState('');
  const [testeAssunto, setTesteAssunto] = useState('');
  const [testeLoading, setTesteLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importando, setImportando] = useState(false);
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const importInputRef = useRef(null);

  const carregarTemplates = useCallback(async () => {
    try {
      const res = await emailTemplatesAPI.listar();
      setTemplates(res.data);
    } catch (err) {
      toast.error('Erro ao carregar templates');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    carregarTemplates();
    emailTemplatesAPI.variaveis().then(r => setVariaveis(r.data)).catch(() => {});
    emailTemplatesAPI.categorias().then(r => setCategorias(r.data)).catch(() => {});
  }, [carregarTemplates]);

  const handleNovo = () => {
    setCurrentTemplate({
      nome: '', assunto: '', corpo_html: '', descricao: '',
      categoria: 'inadimplencia', ativo: true, envio_automatico: false, regra_dias_atraso: null,
    });
    setEditorTab('code');
    setView('edit');
  };

  const handleNovoFromPadrao = () => {
    setCurrentTemplate({ ...TEMPLATE_PADRAO_60_DIAS });
    setEditorTab('code');
    setView('edit');
  };

  const handleEditar = async (id) => {
    try {
      const res = await emailTemplatesAPI.obter(id);
      setCurrentTemplate(res.data);
      setEditorTab('code');
      setView('edit');
    } catch (err) {
      toast.error('Erro ao carregar template');
    }
  };

  const handleDuplicar = async (id) => {
    try {
      const res = await emailTemplatesAPI.obter(id);
      const { nome, assunto, corpo_html, descricao, categoria, envio_automatico, regra_dias_atraso } = res.data;
      setCurrentTemplate({
        nome: `${nome} (cópia)`, assunto, corpo_html, descricao,
        categoria: categoria || 'inadimplencia', ativo: true, envio_automatico, regra_dias_atraso,
      });
      setEditorTab('code');
      setView('edit');
    } catch (err) {
      toast.error('Erro ao duplicar template');
    }
  };

  const handleDeletar = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este template?')) return;
    try {
      await emailTemplatesAPI.deletar(id);
      toast.success('Template excluído');
      carregarTemplates();
    } catch (err) {
      toast.error('Erro ao excluir template');
    }
  };

  const handleSalvar = async () => {
    if (!currentTemplate.nome || !currentTemplate.assunto || !currentTemplate.corpo_html) {
      toast.error('Preencha nome, assunto e corpo do template');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        nome: currentTemplate.nome,
        assunto: currentTemplate.assunto,
        corpo_html: currentTemplate.corpo_html,
        descricao: currentTemplate.descricao || null,
        categoria: currentTemplate.categoria || 'inadimplencia',
        ativo: currentTemplate.ativo,
        envio_automatico: currentTemplate.envio_automatico,
        regra_dias_atraso: currentTemplate.regra_dias_atraso || null,
      };
      if (currentTemplate.id) {
        await emailTemplatesAPI.atualizar(currentTemplate.id, payload);
        toast.success('Template atualizado');
      } else {
        const res = await emailTemplatesAPI.criar(payload);
        setCurrentTemplate({ ...currentTemplate, id: res.data.id });
        toast.success('Template criado');
      }
      carregarTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar template');
    }
    setSaving(false);
  };

  const handlePreviewReal = async () => {
    if (!previewMatRf.trim()) {
      toast.error('Digite a matrícula do RF');
      return;
    }
    if (!currentTemplate?.id) {
      toast.error('Salve o template primeiro');
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await emailTemplatesAPI.preview(currentTemplate.id, previewMatRf.trim());
      setPreviewHtml(res.data.corpo_html);
      setPreviewAssunto(res.data.assunto);
      setView('preview_real');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao gerar preview');
    }
    setPreviewLoading(false);
  };

  const handleTestarDadosReais = async () => {
    if (!testeMatricula.trim()) {
      toast.error('Digite a matrícula do RF para testar');
      return;
    }
    setTesteLoading(true);
    setTesteHtml('');
    setTesteAssunto('');
    try {
      // Se template já salvo, usa API (dados reais do banco)
      if (currentTemplate?.id) {
        const res = await emailTemplatesAPI.preview(currentTemplate.id, testeMatricula.trim());
        setTesteHtml(res.data.corpo_html);
        setTesteAssunto(res.data.assunto);
      } else {
        // Template ainda não salvo: busca dados pelo endpoint de preview criando temp
        // Salva temporariamente para gerar preview
        const payload = {
          nome: currentTemplate?.nome || 'Teste',
          assunto: currentTemplate?.assunto || '',
          corpo_html: currentTemplate?.corpo_html || '',
          descricao: currentTemplate?.descricao || null,
          categoria: currentTemplate?.categoria || 'inadimplencia',
          ativo: false,
          envio_automatico: false,
          regra_dias_atraso: null,
        };
        const criado = await emailTemplatesAPI.criar(payload);
        const res = await emailTemplatesAPI.preview(criado.data.id, testeMatricula.trim());
        await emailTemplatesAPI.deletar(criado.data.id);
        setTesteHtml(res.data.corpo_html);
        setTesteAssunto(res.data.assunto);
      }
      toast.success('Preview gerado com dados reais!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Beneficiário não encontrado ou erro ao gerar preview');
    }
    setTesteLoading(false);
  };

  const inserirVariavel = (chave) => {
    const textarea = editorRef.current;
    if (!textarea) {
      setCurrentTemplate({ ...currentTemplate, corpo_html: (currentTemplate.corpo_html || '') + chave });
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = currentTemplate.corpo_html;
    const newText = text.substring(0, start) + chave + text.substring(end);
    setCurrentTemplate({ ...currentTemplate, corpo_html: newText });
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = start + chave.length;
    }, 0);
  };

  const handleUploadImagem = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(ext)) {
      toast.error('Formato não permitido. Use PNG, JPG, GIF, SVG ou WebP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande (máx 5MB)');
      return;
    }
    setUploading(true);
    try {
      const res = await emailTemplatesAPI.uploadImagem(file);
      const tagHtml = res.data.tag_html;
      inserirVariavel(tagHtml);
      toast.success('Imagem inserida!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar imagem');
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleImportarDocumento = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
      toast.error('Use PDF, PNG, JPG, GIF ou WebP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(`Arquivo muito grande (${(file.size / 1024 / 1024).toFixed(1)}MB). Máximo: 5MB. Comprima o PDF ou use imagem menor.`);
      return;
    }
    setImportando(true);
    const toastId = toast.loading('IA analisando documento e gerando HTML... (pode levar até 30s)');
    try {
      const res = await emailTemplatesAPI.importarDocumento(file);
      const { corpo_html, assunto_sugerido } = res.data;

      if (currentTemplate) {
        setCurrentTemplate({
          ...currentTemplate,
          corpo_html: corpo_html,
          assunto: assunto_sugerido || currentTemplate.assunto,
        });
      } else {
        setCurrentTemplate({
          nome: file.name.replace(/\.[^.]+$/, ''),
          assunto: assunto_sugerido || '',
          corpo_html: corpo_html,
          descricao: `Importado de ${file.name}`,
          categoria: 'inadimplencia',
          ativo: true,
          envio_automatico: false,
          regra_dias_atraso: null,
        });
        setView('edit');
      }
      setEditorTab('preview');
      toast.success('Template importado! Revise na aba Visualização.', { id: toastId });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao importar documento', { id: toastId });
    }
    setImportando(false);
    if (importInputRef.current) importInputRef.current.value = '';
  };

  // ========== VIEW: LISTA ==========
  if (view === 'list') {
    return (
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Título */}
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{ margin: 0, color: '#1a1a2e', fontSize: '22px' }}>
            <Mail size={22} style={{ marginRight: '8px', verticalAlign: 'text-bottom' }} />
            Templates de Email
          </h2>
          <p style={{ margin: '4px 0 0', color: '#666', fontSize: '14px' }}>
            Crie templates de notificação e vincule a um serviço (cobrança, cancelamento, etc.)
          </p>
        </div>

        {/* Botões de ação */}
        <div style={{
          display: 'flex', gap: '10px', marginBottom: '24px', flexWrap: 'wrap',
          padding: '16px', background: '#f8f9fa', borderRadius: '10px', border: '1px solid #eee',
        }}>
          <input ref={importInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
            onChange={handleImportarDocumento} style={{ display: 'none' }} />
          <button onClick={() => importInputRef.current?.click()} disabled={importando} style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '10px 18px', background: '#7b1fa2', color: '#fff',
            border: 'none', borderRadius: '8px', cursor: 'pointer',
            fontWeight: 600, fontSize: '13px', opacity: importando ? 0.6 : 1,
          }}>
            <Upload size={15} /> {importando ? 'IA processando...' : 'Importar PDF/Imagem (IA)'}
          </button>
          <button onClick={handleNovoFromPadrao} style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '10px 18px', background: '#fff', color: '#20a490',
            border: '1px solid #20a490', borderRadius: '8px', cursor: 'pointer',
            fontWeight: 600, fontSize: '13px',
          }}>
            <Zap size={15} /> Criar "Atraso 60 dias"
          </button>
          <button onClick={handleNovo} style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '10px 18px', background: '#20a490', color: '#fff',
            border: 'none', borderRadius: '8px', cursor: 'pointer',
            fontWeight: 600, fontSize: '13px',
          }}>
            <Plus size={15} /> Novo Template em Branco
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div className="spinner" style={{ width: '32px', height: '32px', margin: '0 auto' }} />
          </div>
        ) : templates.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '60px', background: '#f8f9fa',
            borderRadius: '12px', border: '2px dashed #ddd',
          }}>
            <Mail size={48} style={{ color: '#ccc', marginBottom: '16px' }} />
            <p style={{ color: '#666', fontSize: '16px', margin: 0 }}>Nenhum template criado ainda</p>
            <p style={{ color: '#999', fontSize: '14px', marginTop: '8px' }}>
              Clique em "Criar Atraso 60 dias" para começar com o template padrão de cobrança
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {templates.map((tpl) => {
              const catCor = categoriaColor(tpl.categoria);
              return (
                <div key={tpl.id} style={{
                  background: '#fff', borderRadius: '10px', padding: '18px 20px',
                  border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, fontSize: '15px', color: '#1a1a2e' }}>{tpl.nome}</span>
                      <span style={{
                        padding: '2px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                        background: catCor.bg, color: catCor.color,
                      }}>
                        {categoriaLabel(tpl.categoria)}
                      </span>
                      <span style={{
                        padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                        background: tpl.ativo ? '#e8f5e9' : '#fce4ec',
                        color: tpl.ativo ? '#2e7d32' : '#c62828',
                      }}>
                        {tpl.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                      {tpl.envio_automatico && (
                        <span style={{
                          padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                          background: '#e3f2fd', color: '#1565c0',
                          display: 'flex', alignItems: 'center', gap: '3px',
                        }}>
                          <Clock size={11} /> Auto {tpl.regra_dias_atraso ? `>${tpl.regra_dias_atraso}d` : ''}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '13px', color: '#666' }}>{tpl.descricao || tpl.assunto}</div>
                    <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                      Por {tpl.criado_por_nome || '—'} em {tpl.criado_em ? new Date(tpl.criado_em).toLocaleDateString('pt-BR') : '—'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => handleEditar(tpl.id)} title="Editar" style={{
                      padding: '8px', background: '#f5f5f5', border: '1px solid #ddd',
                      borderRadius: '6px', cursor: 'pointer', color: '#555',
                    }}><Edit3 size={16} /></button>
                    <button onClick={() => handleDuplicar(tpl.id)} title="Duplicar" style={{
                      padding: '8px', background: '#f5f5f5', border: '1px solid #ddd',
                      borderRadius: '6px', cursor: 'pointer', color: '#555',
                    }}><Copy size={16} /></button>
                    <button onClick={() => handleDeletar(tpl.id)} title="Excluir" style={{
                      padding: '8px', background: '#fff5f5', border: '1px solid #ffcdd2',
                      borderRadius: '6px', cursor: 'pointer', color: '#c62828',
                    }}><Trash2 size={16} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ========== VIEW: PREVIEW REAL (dados do banco) ==========
  if (view === 'preview_real') {
    return (
      <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <button onClick={() => setView('edit')} style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '8px 14px', background: '#f5f5f5', border: '1px solid #ddd',
            borderRadius: '8px', cursor: 'pointer', fontSize: '14px',
          }}>
            <ChevronLeft size={16} /> Voltar ao Editor
          </button>
          <h3 style={{ margin: 0, color: '#1a1a2e' }}>
            <Eye size={18} style={{ marginRight: '6px', verticalAlign: 'text-bottom' }} />
            Preview com dados reais (Mat. RF: {previewMatRf})
          </h3>
        </div>
        <div style={{
          background: '#f5f5f5', padding: '12px 16px', borderRadius: '8px 8px 0 0',
          border: '1px solid #ddd', borderBottom: 'none', fontSize: '14px',
        }}>
          <strong>Assunto:</strong> {previewAssunto}
        </div>
        <div style={{
          background: '#fff', border: '1px solid #ddd', borderRadius: '0 0 8px 8px',
          padding: '24px', minHeight: '400px',
        }} dangerouslySetInnerHTML={{ __html: previewHtml }} />
      </div>
    );
  }

  // ========== VIEW: EDITOR ==========
  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <button onClick={() => { setView('list'); setCurrentTemplate(null); }} style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '8px 14px', background: '#f5f5f5', border: '1px solid #ddd',
          borderRadius: '8px', cursor: 'pointer', fontSize: '14px',
        }}>
          <ChevronLeft size={16} /> Voltar
        </button>
        <h3 style={{ margin: 0, color: '#1a1a2e' }}>
          {currentTemplate?.id ? 'Editar Template' : 'Novo Template'}
        </h3>
        <button onClick={handleSalvar} disabled={saving} style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '10px 20px', background: '#20a490', color: '#fff',
          border: 'none', borderRadius: '8px', cursor: 'pointer',
          fontWeight: 600, fontSize: '14px', opacity: saving ? 0.6 : 1,
        }}>
          <Save size={16} /> {saving ? 'Salvando...' : 'Salvar'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '20px' }}>
        {/* ===== Coluna principal ===== */}
        <div>
          {/* Campos básicos */}
          <div style={{
            background: '#fff', borderRadius: '10px', padding: '20px',
            border: '1px solid #e0e0e0', marginBottom: '16px',
          }}>
            {/* Linha 1: Nome + Categoria */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#555', marginBottom: '4px' }}>
                  Nome do Template *
                </label>
                <input type="text" value={currentTemplate?.nome || ''} onChange={(e) => setCurrentTemplate({ ...currentTemplate, nome: e.target.value })}
                  placeholder="Ex: Atraso superior a 60 dias" style={{
                    width: '100%', padding: '10px 12px', border: '1px solid #ddd',
                    borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box',
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#555', marginBottom: '4px' }}>
                  <Tag size={13} style={{ marginRight: '3px', verticalAlign: 'text-bottom' }} />
                  Para qual serviço? *
                </label>
                <select value={currentTemplate?.categoria || 'inadimplencia'} onChange={(e) => setCurrentTemplate({ ...currentTemplate, categoria: e.target.value })}
                  style={{
                    width: '100%', padding: '10px 12px', border: '1px solid #ddd',
                    borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box',
                    background: '#fff',
                  }}>
                  {categorias.length > 0 ? categorias.map(c => (
                    <option key={c.valor} value={c.valor}>{c.label}</option>
                  )) : (
                    <>
                      <option value="inadimplencia">Cobrança / Inadimplência</option>
                      <option value="boas_vindas">Boas-vindas</option>
                      <option value="cancelamento">Cancelamento de Plano</option>
                      <option value="reativacao">Reativação de Plano</option>
                      <option value="geral">Notificação Geral</option>
                    </>
                  )}
                </select>
              </div>
            </div>

            {/* Linha 2: Descrição */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#555', marginBottom: '4px' }}>
                Descrição (opcional)
              </label>
              <input type="text" value={currentTemplate?.descricao || ''} onChange={(e) => setCurrentTemplate({ ...currentTemplate, descricao: e.target.value })}
                placeholder="Breve descrição do propósito deste template" style={{
                  width: '100%', padding: '10px 12px', border: '1px solid #ddd',
                  borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Linha 3: Assunto */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#555', marginBottom: '4px' }}>
                Assunto do Email *
                <span style={{ fontWeight: 400, color: '#999', marginLeft: '6px', fontSize: '12px' }}>
                  (pode usar variáveis como {'{NOME_BENEFICIARIO}'})
                </span>
              </label>
              <input type="text" value={currentTemplate?.assunto || ''} onChange={(e) => setCurrentTemplate({ ...currentTemplate, assunto: e.target.value })}
                placeholder="Ex: NOTIFICAÇÃO - Débitos pendentes - {NOME_BENEFICIARIO}" style={{
                  width: '100%', padding: '10px 12px', border: '1px solid #ddd',
                  borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Linha 4: Opções de envio */}
            <div style={{
              display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap',
              padding: '12px', background: '#f8f9fa', borderRadius: '8px',
            }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' }}>
                <input type="checkbox" checked={currentTemplate?.ativo || false}
                  onChange={(e) => setCurrentTemplate({ ...currentTemplate, ativo: e.target.checked })} />
                Ativo
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' }}>
                <input type="checkbox" checked={currentTemplate?.envio_automatico || false}
                  onChange={(e) => setCurrentTemplate({ ...currentTemplate, envio_automatico: e.target.checked })} />
                <Clock size={14} /> Envio Automático
              </label>
              {currentTemplate?.envio_automatico && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '13px', color: '#555' }}>Enviar quando atraso maior que</span>
                  <input type="number" value={currentTemplate?.regra_dias_atraso || ''} placeholder="60"
                    onChange={(e) => setCurrentTemplate({ ...currentTemplate, regra_dias_atraso: parseInt(e.target.value) || null })}
                    style={{ width: '70px', padding: '6px 8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', textAlign: 'center' }}
                  />
                  <span style={{ fontSize: '13px', color: '#555' }}>dias</span>
                </div>
              )}
            </div>
          </div>

          {/* Editor com 3 abas: Visual / HTML / Visualização */}
          <div style={{ background: '#fff', borderRadius: '10px', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
            {/* Abas */}
            <div style={{ display: 'flex', borderBottom: '1px solid #e0e0e0' }}>
              {[
                { key: 'visual', label: 'Editor Visual', icon: <Edit3 size={14} />, color: '#e65100' },
                { key: 'code', label: 'Código HTML', icon: <Code size={14} />, color: '#20a490' },
                { key: 'preview', label: 'Visualização', icon: <Eye size={14} />, color: '#1565c0' },
              ].map(tab => (
                <button key={tab.key} onClick={() => {
                  // Ao sair do visual, capturar o HTML editado
                  if (editorTab === 'visual' && tab.key !== 'visual') {
                    const el = document.getElementById('visual-editor');
                    if (el) setCurrentTemplate({ ...currentTemplate, corpo_html: el.innerHTML });
                  }
                  setEditorTab(tab.key);
                }} style={{
                  flex: 1, padding: '12px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                  background: editorTab === tab.key ? '#fff' : '#f8f9fa',
                  color: editorTab === tab.key ? tab.color : '#888',
                  borderBottom: editorTab === tab.key ? `2px solid ${tab.color}` : '2px solid transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                }}>
                  {tab.icon} {tab.label}
                </button>
              ))}
            </div>

            {/* Aba: Editor Visual (WYSIWYG) */}
            {editorTab === 'visual' && (
              <div style={{ padding: '0' }}>
                {/* Toolbar */}
                <div style={{
                  display: 'flex', gap: '2px', padding: '8px 12px', background: '#f5f5f5',
                  borderBottom: '1px solid #e0e0e0', flexWrap: 'wrap', alignItems: 'center',
                }}>
                  {[
                    { cmd: 'bold', label: 'N', title: 'Negrito', style: { fontWeight: 'bold' } },
                    { cmd: 'italic', label: 'I', title: 'Itálico', style: { fontStyle: 'italic' } },
                    { cmd: 'underline', label: 'S', title: 'Sublinhado', style: { textDecoration: 'underline' } },
                    { cmd: 'strikethrough', label: 'T', title: 'Tachado', style: { textDecoration: 'line-through' } },
                  ].map(btn => (
                    <button key={btn.cmd} title={btn.title} onMouseDown={(e) => {
                      e.preventDefault();
                      document.execCommand(btn.cmd, false, null);
                    }} style={{
                      padding: '4px 10px', background: '#fff', border: '1px solid #ddd',
                      borderRadius: '4px', cursor: 'pointer', fontSize: '14px', minWidth: '32px',
                      ...btn.style,
                    }}>
                      {btn.label}
                    </button>
                  ))}

                  <span style={{ width: '1px', height: '24px', background: '#ddd', margin: '0 6px' }} />

                  {/* Alinhamento */}
                  {[
                    { cmd: 'justifyLeft', label: '⫷', title: 'Alinhar esquerda' },
                    { cmd: 'justifyCenter', label: '⫿', title: 'Centralizar' },
                    { cmd: 'justifyRight', label: '⫸', title: 'Alinhar direita' },
                  ].map(btn => (
                    <button key={btn.cmd} title={btn.title} onMouseDown={(e) => {
                      e.preventDefault();
                      document.execCommand(btn.cmd, false, null);
                    }} style={{
                      padding: '4px 8px', background: '#fff', border: '1px solid #ddd',
                      borderRadius: '4px', cursor: 'pointer', fontSize: '14px',
                    }}>
                      {btn.label}
                    </button>
                  ))}

                  <span style={{ width: '1px', height: '24px', background: '#ddd', margin: '0 6px' }} />

                  {/* Listas */}
                  <button title="Lista com marcadores" onMouseDown={(e) => {
                    e.preventDefault();
                    document.execCommand('insertUnorderedList', false, null);
                  }} style={{
                    padding: '4px 8px', background: '#fff', border: '1px solid #ddd',
                    borderRadius: '4px', cursor: 'pointer', fontSize: '13px',
                  }}>• Lista</button>

                  <button title="Linha horizontal" onMouseDown={(e) => {
                    e.preventDefault();
                    document.execCommand('insertHorizontalRule', false, null);
                  }} style={{
                    padding: '4px 8px', background: '#fff', border: '1px solid #ddd',
                    borderRadius: '4px', cursor: 'pointer', fontSize: '13px',
                  }}>Linha divisória</button>

                  <span style={{ width: '1px', height: '24px', background: '#ddd', margin: '0 6px' }} />

                  {/* Cores */}
                  <label title="Cor do texto" style={{
                    padding: '4px 8px', background: '#fff', border: '1px solid #ddd',
                    borderRadius: '4px', cursor: 'pointer', fontSize: '12px',
                    display: 'flex', alignItems: 'center', gap: '4px',
                  }}>
                    A
                    <input type="color" defaultValue="#000000" onChange={(e) => {
                      document.execCommand('foreColor', false, e.target.value);
                    }} style={{ width: '20px', height: '20px', border: 'none', cursor: 'pointer', padding: 0 }} />
                  </label>

                  <label title="Cor de fundo" style={{
                    padding: '4px 8px', background: '#fff', border: '1px solid #ddd',
                    borderRadius: '4px', cursor: 'pointer', fontSize: '12px',
                    display: 'flex', alignItems: 'center', gap: '4px',
                  }}>
                    Fundo
                    <input type="color" defaultValue="#ffffff" onChange={(e) => {
                      document.execCommand('hiliteColor', false, e.target.value);
                    }} style={{ width: '20px', height: '20px', border: 'none', cursor: 'pointer', padding: 0 }} />
                  </label>

                  <span style={{ width: '1px', height: '24px', background: '#ddd', margin: '0 6px' }} />

                  {/* Tamanho fonte */}
                  <select title="Tamanho da fonte" onChange={(e) => {
                    document.execCommand('fontSize', false, e.target.value);
                  }} defaultValue="3" style={{
                    padding: '4px 6px', border: '1px solid #ddd', borderRadius: '4px',
                    fontSize: '12px', cursor: 'pointer',
                  }}>
                    <option value="1">Pequeno</option>
                    <option value="2">Menor</option>
                    <option value="3">Normal</option>
                    <option value="4">Médio</option>
                    <option value="5">Grande</option>
                    <option value="6">Muito grande</option>
                    <option value="7">Enorme</option>
                  </select>

                  <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#999' }}>
                    Edite o texto diretamente abaixo
                  </span>
                </div>

                {/* Área editável */}
                <div
                  id="visual-editor"
                  contentEditable
                  suppressContentEditableWarning
                  dangerouslySetInnerHTML={{ __html: currentTemplate?.corpo_html || '' }}
                  onBlur={(e) => {
                    setCurrentTemplate({ ...currentTemplate, corpo_html: e.currentTarget.innerHTML });
                  }}
                  style={{
                    padding: '24px', minHeight: '500px', outline: 'none',
                    fontSize: '14px', lineHeight: '1.6', cursor: 'text',
                  }}
                />
              </div>
            )}

            {/* Aba: Código HTML */}
            {editorTab === 'code' && (
              <div style={{ padding: '16px' }}>
                <textarea ref={editorRef} value={currentTemplate?.corpo_html || ''}
                  onChange={(e) => setCurrentTemplate({ ...currentTemplate, corpo_html: e.target.value })}
                  style={{
                    width: '100%', minHeight: '500px', padding: '14px',
                    border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px',
                    fontFamily: 'Consolas, Monaco, monospace', resize: 'vertical',
                    lineHeight: '1.5', boxSizing: 'border-box', background: '#fafafa',
                  }}
                />
              </div>
            )}

            {/* Aba: Visualização */}
            {editorTab === 'preview' && (
              <div style={{ padding: '20px' }}>

                {/* Bloco de teste com dados reais — sempre visível */}
                <div style={{
                  marginBottom: '20px', padding: '14px 16px',
                  background: 'linear-gradient(135deg, #e8f5e9, #f1f8e9)',
                  borderRadius: '10px', border: '1px solid #a5d6a7',
                }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#2e7d32', marginBottom: '8px' }}>
                    🔍 Testar com beneficiário real
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={testeMatricula}
                      onChange={(e) => { setTesteMatricula(e.target.value); setTesteHtml(''); }}
                      onKeyDown={(e) => e.key === 'Enter' && handleTestarDadosReais()}
                      placeholder="Matrícula do RF (ex: 12345)"
                      style={{
                        flex: 1, padding: '9px 12px', border: '1px solid #81c784',
                        borderRadius: '7px', fontSize: '14px', outline: 'none',
                      }}
                    />
                    <button onClick={handleTestarDadosReais} disabled={testeLoading} style={{
                      padding: '9px 20px', background: testeLoading ? '#a5d6a7' : '#2e7d32',
                      color: '#fff', border: 'none', borderRadius: '7px',
                      cursor: testeLoading ? 'default' : 'pointer',
                      fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap',
                    }}>
                      {testeLoading ? 'Buscando...' : 'Gerar Preview'}
                    </button>
                    {testeHtml && (
                      <button onClick={() => { setTesteHtml(''); setTesteMatricula(''); }} style={{
                        padding: '9px 12px', background: '#fff', color: '#666',
                        border: '1px solid #ccc', borderRadius: '7px', cursor: 'pointer', fontSize: '13px',
                      }}>
                        Limpar
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: '11px', color: '#558b2f', marginTop: '6px' }}>
                    Substitui as variáveis pelos dados reais do beneficiário, ideal para validar o template antes de enviar.
                  </div>
                </div>

                {/* Preview: dados reais (se testado) ou dados de exemplo */}
                {testeHtml ? (
                  <>
                    <div style={{
                      background: '#e8f5e9', padding: '10px 14px', borderRadius: '6px 6px 0 0',
                      border: '1px solid #a5d6a7', borderBottom: 'none', fontSize: '13px',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                      <span><strong>Assunto:</strong> {testeAssunto}</span>
                      <span style={{ fontSize: '11px', color: '#2e7d32', fontWeight: 600 }}>
                        ✓ Dados reais · Mat. {testeMatricula}
                      </span>
                    </div>
                    <div style={{
                      border: '1px solid #a5d6a7', borderRadius: '0 0 6px 6px',
                      padding: '20px', minHeight: '400px', background: '#fff',
                    }} dangerouslySetInnerHTML={{ __html: testeHtml }} />
                  </>
                ) : (
                  <>
                    <div style={{
                      background: '#f5f5f5', padding: '10px 14px', borderRadius: '6px 6px 0 0',
                      border: '1px solid #ddd', borderBottom: 'none', fontSize: '13px', color: '#555',
                      display: 'flex', justifyContent: 'space-between',
                    }}>
                      <span><strong>Assunto:</strong> {previewLocal(currentTemplate?.assunto || '')}</span>
                      <span style={{ fontSize: '11px', color: '#999' }}>Dados de exemplo</span>
                    </div>
                    <div style={{
                      border: '1px solid #ddd', borderRadius: '0 0 6px 6px',
                      padding: '20px', minHeight: '400px', background: '#fff',
                    }} dangerouslySetInnerHTML={{ __html: previewLocal(currentTemplate?.corpo_html || '') }} />
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ===== Sidebar: Variáveis ===== */}
        <div>
          <div style={{
            background: '#fff', borderRadius: '10px', padding: '16px',
            border: '1px solid #e0e0e0', position: 'sticky', top: '80px',
          }}>
            <h4 style={{ margin: '0 0 6px', fontSize: '14px', color: '#1a1a2e' }}>
              Inserir Variáveis
            </h4>
            <p style={{ fontSize: '12px', color: '#888', margin: '0 0 14px', lineHeight: '1.4' }}>
              Clique em uma variável para inseri-la no HTML na posição do cursor.
              No envio, cada variável é substituída pelos dados reais do beneficiário.
            </p>

            <div style={{ display: 'grid', gap: '6px' }}>
              {variaveis.map((v) => (
                <button key={v.chave} onClick={() => inserirVariavel(v.chave)} style={{
                  textAlign: 'left', padding: '10px 12px', background: '#f8f9fa',
                  border: '1px solid #e8e8e8', borderRadius: '8px', cursor: 'pointer',
                  transition: 'all 0.15s', display: 'flex', flexDirection: 'column', gap: '3px',
                }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#e8f5e9'; e.currentTarget.style.borderColor = '#a5d6a7'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#f8f9fa'; e.currentTarget.style.borderColor = '#e8e8e8'; }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#20a490', fontFamily: 'monospace' }}>
                      {v.chave}
                    </span>
                    <span style={{ fontSize: '10px', color: '#20a490', fontWeight: 600, background: '#e8f5e9', padding: '1px 6px', borderRadius: '4px' }}>
                      INSERIR
                    </span>
                  </div>
                  <div style={{ fontSize: '11px', color: '#888' }}>{v.descricao}</div>
                </button>
              ))}
            </div>

            {/* Importar documento via IA */}
            <div style={{
              marginTop: '16px', padding: '14px', background: 'linear-gradient(135deg, #f3e5f5, #ede7f6)',
              borderRadius: '10px', border: '1px solid #ce93d8',
            }}>
              <h4 style={{ margin: '0 0 6px', fontSize: '14px', color: '#6a1b9a' }}>
                <Zap size={14} style={{ marginRight: '4px', verticalAlign: 'text-bottom' }} />
                Importar via IA
              </h4>
              <p style={{ fontSize: '11px', color: '#7b1fa2', margin: '0 0 10px', lineHeight: '1.4' }}>
                Envie um PDF ou imagem de um template pronto e a IA gera o HTML com as variáveis mapeadas automaticamente.
              </p>
              <input ref={importInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                onChange={handleImportarDocumento} style={{ display: 'none' }} />
              <button onClick={() => importInputRef.current?.click()} disabled={importando} style={{
                width: '100%', padding: '10px', background: importando ? '#e1bee7' : '#7b1fa2', color: '#fff',
                border: 'none', borderRadius: '8px', cursor: importando ? 'default' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                fontSize: '13px', fontWeight: 600,
              }}>
                <Upload size={16} />
                {importando ? 'IA processando...' : 'Enviar PDF/Imagem'}
              </button>
            </div>

            {/* Upload de imagem */}
            <div style={{ marginTop: '16px' }}>
              <h4 style={{ margin: '0 0 8px', fontSize: '14px', color: '#1a1a2e' }}>
                <Image size={14} style={{ marginRight: '4px', verticalAlign: 'text-bottom' }} />
                Inserir Imagem
              </h4>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp"
                onChange={handleUploadImagem}
                style={{ display: 'none' }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                style={{
                  width: '100%', padding: '10px', background: uploading ? '#f5f5f5' : '#fff',
                  border: '2px dashed #ccc', borderRadius: '8px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  fontSize: '13px', color: '#666', transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => { if (!uploading) { e.currentTarget.style.borderColor = '#20a490'; e.currentTarget.style.color = '#20a490'; } }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#ccc'; e.currentTarget.style.color = '#666'; }}
              >
                <Upload size={16} />
                {uploading ? 'Enviando...' : 'Clique para enviar imagem'}
              </button>
              <p style={{ fontSize: '11px', color: '#999', margin: '4px 0 0', textAlign: 'center' }}>
                PNG, JPG, GIF, SVG ou WebP (máx 5MB)
              </p>
            </div>

            {/* Como funciona */}
            <div style={{
              marginTop: '16px', padding: '12px', background: '#f0f7ff',
              borderRadius: '8px', border: '1px solid #bbdefb', fontSize: '12px', color: '#1565c0', lineHeight: '1.5',
            }}>
              <strong style={{ display: 'block', marginBottom: '4px' }}>Como funciona?</strong>
              1. Escolha a <strong>categoria</strong> (serviço) do template<br />
              2. Escreva o HTML e insira as <strong>variáveis</strong> onde desejar<br />
              3. Na aba <strong>"Visualização"</strong> veja como ficará<br />
              4. <strong>Salve</strong> e use no Dashboard de Inadimplência
            </div>

            <div style={{
              marginTop: '10px', padding: '10px', background: '#fff8e1',
              borderRadius: '6px', border: '1px solid #ffe082', fontSize: '12px', color: '#795548',
            }}>
              <AlertTriangle size={13} style={{ marginRight: '4px', verticalAlign: 'text-bottom' }} />
              <strong>Importante:</strong> A variável <code style={{ background: '#fff3e0', padding: '1px 4px', borderRadius: '3px' }}>{'{TABELA_DEBITOS}'}</code> gera automaticamente a tabela com todos os boletos em atraso do beneficiário.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplatesEmail;
